import type { OrgFormValues } from '@/config/organizations';
import { useEditOrganization } from '@/config/organizations/useEditOrganization';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useNavigate } from '@tanstack/react-router';
import clsx from 'clsx';
import React, { useCallback } from 'react';
import type { FC, SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';

import globalStyles from '../../../styles/index.module.css';

import { BasicInfoSection } from './BasicInfoSection';
import { ContactFields } from './ContactFields';
import styles from './styles.module.css';
import { SubscriptionSection } from './SubscriptionSection';

interface OrganizationDetailsEditProps {
  initialValues: OrgFormValues;
  /** View-only render, set from `!isGlobalSuperAdmin()`. `PUT /orgs/{org_id}` is restricted to platform super
   *  admins (`user.is_super_admin`, independent of org roles), so anyone else would be rejected on save. */
  isReadOnly?: boolean;
  onCancel?: () => void;
  onSave?: (values: OrgFormValues) => void;
  orgId: string;
}

export const OrganizationDetailsEdit: FC<OrganizationDetailsEditProps> = ({
  initialValues,
  isReadOnly = false,
  onCancel,
  onSave,
  orgId,
}) => {
  const { form } = useEditOrganization({ orgId, initialValues, onSave });
  const { t } = useTranslation('orgs');
  const navigate = useNavigate();

  const onCancelHandler = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      navigate({ to: '/organizations' });
    }
  }, [navigate, onCancel]);

  const onSubmitHandler = async (e: SubmitEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();

    // Hiding Save is presentation, not a guard — a submit event can still reach here without it.
    if (isReadOnly) return;

    await form.handleSubmit();
  };

  return (
    <form className={styles.editFormContainer} onSubmit={onSubmitHandler}>
      <form.Subscribe selector={s => s.values.name}>{name => <Text as="h1">{name}</Text>}</form.Subscribe>

      {isReadOnly && (
        <Text as="p" data-testid="org-read-only-notice">
          {t('edit_org_read_only')}
        </Text>
      )}

      <Stack className={clsx(globalStyles.scrollbars, styles.editFormControlsContainer)} gap={4} justifyContent="start">
        <BasicInfoSection form={form} isReadOnly={isReadOnly} />

        <Grid columns={2} fullWidth gap={8}>
          <ContactFields form={form} isReadOnly={isReadOnly} section="primaryContact" />
          <ContactFields form={form} isReadOnly={isReadOnly} section="itContact" />
        </Grid>

        <SubscriptionSection form={form} isReadOnly={isReadOnly} />
      </Stack>

      <form.Subscribe selector={s => [s.isDirty, s.canSubmit, s.isSubmitting]}>
        {([isDirty, canSubmit, isSubmitting]) => (
          <Grid className={styles.editFormFooter} columns={2} fullWidth gap={8}>
            <Button data-testid="cancel-btn" onPress={onCancelHandler} type="button" visual="default">
              {isReadOnly ? t('edit_org_back') : t('create_org_cancel')}
            </Button>

            {!isReadOnly && (
              <Button data-testid="save-btn" isDisabled={!isDirty || !canSubmit} type="submit" visual="primary">
                {isSubmitting ? <>{t('edit_org_saving')}&hellip;</> : t('edit_org_save_all')}
              </Button>
            )}
          </Grid>
        )}
      </form.Subscribe>
    </form>
  );
};
